import {useQuery} from "@tanstack/react-query";
import axios from "axios";
import {useBrand} from "../../component/BrandProvider";
import {useParams} from "react-router";

interface Site {
	Site_Code: string;
	Site_Name: number;
}

interface SiteData {
	label: string;
	value: number;
}

const fetchSites = async (baseUrl: string): Promise<SiteData[]> => {
	const siteData = await axios.get(`${baseUrl}/api/Site/Get-All-Sites`);
	const data: Site[] = siteData.data;

	const removeItems = [
		"Clean Freak Office",
		"zDoNotUseClean Freak - Ventura",
		"Clean Freak WebConnect",
		"Rainstorm Office",
		"zDoNotUse Lexington Richmond",
		"zDoNotUseLexington Meijer East",
		"zDoNotUseLexington Meijer West",
		"zDoNotUse Amelia W Main",
	];

	return data
		.filter((site: any) => !removeItems.includes(site.Site_Name))
		.map((site: any) => ({
			label: site.Site_Name,
			value: site.Site_Code,
			name: site.Corporate_Name.replace("\r", "").replace("\n", "")
		}));
};

const fetchSitesTableMode = async (baseUrl: string): Promise<any> => {
	const siteData = await axios.get(`${baseUrl}/api/Site/Get-All-Sites`);
	const data: Site[] = siteData.data;

	const removeItems = [
		"Clean Freak Office",
		"zDoNotUseClean Freak - Ventura",
		"Clean Freak WebConnect",
		"Rainstorm Office",
		"zDoNotUse Lexington Richmond",
		"zDoNotUseLexington Meijer East",
		"zDoNotUseLexington Meijer West",
		"zDoNotUse Amelia W Main",
	];

	return Object.assign(
		{},
		...data
			.filter((site: any) => !removeItems.includes(site.Site_Name))
			.map((site: any) => (
				{
					[site.Site_Code]: site.Site_Name.includes("-") 
						? 
							site.Site_Name.split(" - ")[1] :
							site.Site_Name.includes("TB ") 
						?
							site.Site_Name.split("TB ")[1] : 
							site.Site_Name.includes("RS") 
						?
							site.Site_Name.split("RS ")[1] : 
							site.Site_Name
				}
			))
	);
};

const fetchSiteCodesTableMode = async (baseUrl: string): Promise<any> => {
	const siteData = await axios.get(`${baseUrl}/api/Site/Get-All-Sites`);
	const data: Site[] = siteData.data;

	const removeItems = [
		"Clean Freak Office",
		"zDoNotUseClean Freak - Ventura",
		"Clean Freak WebConnect",
		"Rainstorm Office",
		"zDoNotUse Lexington Richmond",
		"zDoNotUseLexington Meijer East",
		"zDoNotUseLexington Meijer West",
		"zDoNotUse Amelia W Main",
	];

	return Object.assign(
		{},
		...data
			.filter((site: any) => !removeItems.includes(site.Site_Name))
			.map((site: any) => (
				{
					[site.Site_Code]: site.Corporate_Name.replace("\r", "").replace("\n", "")
				}
			))
	);
};

export const useGetSites = () => {
	const {url} = useBrand();

	return useQuery<SiteData[]>({
		queryKey: ["sites", url],
		queryFn: () => fetchSites(url),
		select: (data: any) => data.map((val: any) => {
			return { 
				"value": val["value"],
				"label": val["label"].includes("-") ? val["label"].split(" - ")[1] :
					val["label"].includes("TB ") ?
						val["label"].split("TB ")[1] : 
					val["label"].includes("RS") ?
					val["label"].split("RS ")[1] : val["label"],
				"corporate_name": val["name"]
			}
		}),
		refetchOnWindowFocus: false,
		staleTime: 24 * 60 * 60 * 1000,
		gcTime: 23 * 59 * 60 * 1000
	});
};

export const useGetSitesTable = () => {
	const {brand} = useParams();
	const {url} = useBrand();

	const urlParam =
		brand === undefined
			? undefined
			: brand === "cleanfreak"
			? process.env.REACT_APP_API_URL_CLEANFREAK
			: process.env.REACT_APP_API_URL_RAINSTORM;

	return useQuery<any>({
		queryKey: ["sites", "table", urlParam || url],
		queryFn: () => fetchSitesTableMode(urlParam || url),
		refetchOnWindowFocus: false,
		staleTime: 24 * 60 * 60 * 1000,
		gcTime: 23 * 59 * 60 * 1000
	});
};

export const useGetSiteCodesTable = () => {
	const {brand} = useParams();
	const {url} = useBrand();

	const urlParam =
		brand === undefined
			? undefined
			: brand === "cleanfreak"
			? process.env.REACT_APP_API_URL_CLEANFREAK
			: process.env.REACT_APP_API_URL_RAINSTORM;

	return useQuery<any>({
		queryKey: ["sites", "codes", urlParam || url],
		queryFn: () => fetchSiteCodesTableMode(urlParam || url),
		refetchOnWindowFocus: false,
		staleTime: 24 * 60 * 60 * 1000,
		gcTime: 23 * 59 * 60 * 1000
	});
};

